import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  getConversations,
  getUnmatchedMessages,
  dismissMessage,
} from "../../../services/crmMessages";
import { getBulkOutreachRecords, getBulkOutreachDetail } from "../../../services/crmBulkOutreach";
import type {
  ConversationSummaryResponse,
  CrmMessageResponse,
  BulkOutreachRecordResponse,
  BulkOutreachDetailResponse,
} from "../../../types/crm";
import { GmailIcon } from "../../../assets/GmailIcon";
import { SlackIcon } from "../../../assets/SlackIcon";
import { MessageIcon } from "../../../assets/MessageIcon";
import { XIcon } from "../../../assets/XIcon";
import { BlueBubblesIcon } from "../../../assets/BlueBubblesIcon";
import { TablePagination } from "../../../components/TablePagination";
import { AssignMessageModal } from "./AssignMessageModal";
import {
  communicationsScrollKey,
  communicationsViewKey,
  loadCrmViewState,
  readPageParam,
  saveCrmViewState,
} from "../utils/crmViewState";
import { useCrmScrollRestoration } from "../hooks/useCrmScrollRestoration";
import "./CommunicationsPage.css";

const PAGE_SIZE = 10;

type Tab = "recent" | "unread" | "unmatched" | "outreach" | "debug";

/** The tabs a user can actually select — a stored value outside this set is ignored. */
const TABS: Tab[] = ["recent", "unread", "unmatched", "outreach"];

const isTab = (value: string | null): value is Tab =>
  value !== null && (TABS as string[]).includes(value);

/** Where the user last was on this page: which tab, and which page of each tab's list. */
const readViewState = (params: URLSearchParams | null) => {
  const tab = params?.get("tab") ?? null;
  return {
    tab: isTab(tab) ? tab : ("recent" as Tab),
    recentPage: readPageParam(params?.get("recentPage") ?? null),
    unreadPage: readPageParam(params?.get("unreadPage") ?? null),
    unmatchedPage: readPageParam(params?.get("unmatchedPage") ?? null),
    outreachPage: readPageParam(params?.get("outreachPage") ?? null),
  };
};

interface ConvoTabState {
  items: ConversationSummaryResponse[];
  totalCount: number;
  currentPage: number;
  isLoaded: boolean;
  loading: boolean;
  error: string;
}

interface UnmatchedTabState {
  items: CrmMessageResponse[];
  totalCount: number;
  currentPage: number;
  isLoaded: boolean;
  loading: boolean;
  error: string;
}

const EMPTY_CONVO: ConvoTabState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  isLoaded: false,
  loading: false,
  error: "",
};

const EMPTY_UNMATCHED: UnmatchedTabState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  isLoaded: false,
  loading: false,
  error: "",
};

function ChannelIcon({ channel }: { channel: string }) {
  switch (channel) {
    case "Gmail":
      return <GmailIcon className="communications-page__channel-icon" />;
    case "Slack":
      return <SlackIcon className="communications-page__channel-icon" />;
    case "Quo":
      return <MessageIcon className="communications-page__channel-icon" />;
    case "X":
    case "Twitter":
      return <XIcon className="communications-page__channel-icon" />;
    case "BlueBubbles":
      return <BlueBubblesIcon className="communications-page__channel-icon" />;
    default:
      return (
        <span className="communications-page__channel-icon-fallback">
          {channel[0]}
        </span>
      );
  }
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

function contactLabel(contactType: string): string {
  if (contactType === "ProspectProfile") return "Player";
  return contactType;
}

export const CommunicationsPage = () => {
  const navigate = useNavigate();
  // Read once, on first render — later renders must not re-hydrate over live state.
  const restoredRef = useRef(readViewState(loadCrmViewState(communicationsViewKey())));
  const restored = restoredRef.current;

  const [activeTab, setActiveTab] = useState<Tab>(restored.tab);
  const [recent, setRecent] = useState<ConvoTabState>({
    ...EMPTY_CONVO,
    currentPage: restored.recentPage,
  });
  const [unread, setUnread] = useState<ConvoTabState>({
    ...EMPTY_CONVO,
    currentPage: restored.unreadPage,
  });
  const [unmatched, setUnmatched] = useState<UnmatchedTabState>({
    ...EMPTY_UNMATCHED,
    currentPage: restored.unmatchedPage,
  });
  const [assigningMessage, setAssigningMessage] = useState<CrmMessageResponse | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Outreach tab state
  const [outreachRecords, setOutreachRecords] = useState<BulkOutreachRecordResponse[]>([]);
  const [outreachTotalCount, setOutreachTotalCount] = useState(0);
  const [outreachPage, setOutreachPage] = useState(restored.outreachPage);
  const [outreachLoading, setOutreachLoading] = useState(false);
  const [outreachLoaded, setOutreachLoaded] = useState(false);
  const [outreachError, setOutreachError] = useState("");
  const [expandedOutreach, setExpandedOutreach] = useState<string | null>(null);
  const [outreachDetail, setOutreachDetail] = useState<BulkOutreachDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dismissing, setDismissing] = useState<string | null>(null);
  const activeTabRef = useRef<Tab>(restored.tab);
  activeTabRef.current = activeTab;

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const loadConvoPage = useCallback(
    async (tab: "recent" | "unread", page: number, silent = false) => {
      const setter = tab === "recent" ? setRecent : setUnread;
      if (!silent) {
        setter((prev) => ({ ...prev, loading: true, error: "" }));
      }
      try {
        const data = await getConversations(page, PAGE_SIZE, tab === "unread");
        setter({
          items: data.items,
          totalCount: data.totalCount,
          currentPage: page,
          isLoaded: true,
          loading: false,
          error: "",
        });
      } catch {
        setter((prev) => ({
          ...prev,
          loading: false,
          error: "Failed to load conversations.",
        }));
      }
    },
    []
  );

  const loadUnmatchedPage = useCallback(async (page: number) => {
    setUnmatched((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      let currentPage = page;
      let data = await getUnmatchedMessages(currentPage, PAGE_SIZE);

      // Working a queue down shrinks it, so the remembered page may no longer exist —
      // as can happen after clearing the last message on it. Land on the last page that
      // does rather than stranding the user on a blank one they have to paginate out of.
      const lastPage = Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE));
      if (data.items.length === 0 && currentPage > lastPage) {
        currentPage = lastPage;
        data = await getUnmatchedMessages(currentPage, PAGE_SIZE);
      }

      setUnmatched({
        items: data.items,
        totalCount: data.totalCount,
        currentPage,
        isLoaded: true,
        loading: false,
        error: "",
      });
    } catch {
      setUnmatched((prev) => ({
        ...prev,
        loading: false,
        error: "Failed to load unmatched messages.",
      }));
    }
  }, []);

  // Auto-refresh recent tab every 30 seconds
  const recentPageRef = useRef(restored.recentPage);
  useEffect(() => {
    recentPageRef.current = recent.currentPage;
  }, [recent.currentPage]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTabRef.current === "recent") {
        loadConvoPage("recent", recentPageRef.current, true);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [loadConvoPage]);

  const loadOutreachPage = useCallback(async (page: number) => {
    setOutreachLoading(true);
    setOutreachError("");
    try {
      const data = await getBulkOutreachRecords(page, PAGE_SIZE);
      setOutreachRecords(data.items);
      setOutreachTotalCount(data.totalCount);
      setOutreachPage(page);
      setOutreachLoaded(true);
    } catch {
      setOutreachError("Failed to load outreach records.");
    } finally {
      setOutreachLoading(false);
    }
  }, []);

  // Initial load — resumes on the tab and page the user left off on. The lists backing
  // the tab bar's badges (recent, unmatched) always load; the lazy tabs only load when
  // they are the one being restored.
  useEffect(() => {
    const { tab, recentPage, unreadPage, unmatchedPage, outreachPage } =
      restoredRef.current;
    loadConvoPage("recent", recentPage);
    loadUnmatchedPage(unmatchedPage);
    if (tab === "unread") loadConvoPage("unread", unreadPage);
    if (tab === "outreach") loadOutreachPage(outreachPage);
  }, [loadConvoPage, loadUnmatchedPage, loadOutreachPage]);

  // Remember the place for the next visit within this browser tab.
  useEffect(() => {
    const params = new URLSearchParams();
    params.set("tab", activeTab);
    if (recent.currentPage > 1) params.set("recentPage", String(recent.currentPage));
    if (unread.currentPage > 1) params.set("unreadPage", String(unread.currentPage));
    if (unmatched.currentPage > 1) params.set("unmatchedPage", String(unmatched.currentPage));
    if (outreachPage > 1) params.set("outreachPage", String(outreachPage));
    saveCrmViewState(communicationsViewKey(), params);
  }, [
    activeTab,
    recent.currentPage,
    unread.currentPage,
    unmatched.currentPage,
    outreachPage,
  ]);

  // The active tab's list has to be on screen before a scroll offset means anything.
  const activeTabState =
    activeTab === "unread"
      ? { isLoaded: unread.isLoaded, loading: unread.loading }
      : activeTab === "unmatched"
        ? { isLoaded: unmatched.isLoaded, loading: unmatched.loading }
        : activeTab === "outreach"
          ? { isLoaded: outreachLoaded, loading: outreachLoading }
          : { isLoaded: recent.isLoaded, loading: recent.loading };

  useCrmScrollRestoration(
    communicationsScrollKey(),
    activeTabState.isLoaded && !activeTabState.loading
  );

  const handleExpandOutreach = async (id: string) => {
    if (expandedOutreach === id) {
      setExpandedOutreach(null);
      setOutreachDetail(null);
      return;
    }
    setExpandedOutreach(id);
    setDetailLoading(true);
    try {
      const detail = await getBulkOutreachDetail(id);
      setOutreachDetail(detail);
    } catch {
      // silent
    } finally {
      setDetailLoading(false);
    }
  };

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    // A tab that hasn't loaded yet opens on its remembered page, not page 1.
    if (tab === "unread" && !unread.isLoaded) {
      loadConvoPage("unread", unread.currentPage);
    }
    if (tab === "unmatched" && !unmatched.isLoaded) {
      loadUnmatchedPage(unmatched.currentPage);
    }
    if (tab === "outreach" && !outreachLoaded) {
      loadOutreachPage(outreachPage);
    }
  };

  // Every jump out of this page carries the way back, so the detail view returns the
  // user to Messages — where the restored tab and page put them back where they were.
  const backToMessages = { from: "/crm/communications", fromLabel: "Messages" };

  const handleConvoClick = (conv: ConversationSummaryResponse) => {
    navigate(
      `/crm/contacts/${conv.contactId}?channel=${conv.channel.toLowerCase()}`,
      { state: backToMessages }
    );
  };

  const handleDismiss = async (id: string) => {
    setDismissing(id);
    try {
      await dismissMessage(id);
      // Reload the current unmatched page so count is accurate
      await loadUnmatchedPage(unmatched.currentPage);
    } catch {
      setUnmatched((prev) => ({
        ...prev,
        error: "Failed to dismiss message.",
      }));
    } finally {
      setDismissing(null);
    }
  };

  const handleAssigned = () => {
    setAssigningMessage(null);
    // Stay on the page being worked through rather than jumping back to the top.
    loadUnmatchedPage(unmatched.currentPage);
  };

  const unreadBadgeCount = unread.isLoaded
    ? unread.totalCount
    : recent.items.filter((c) => c.unreadCount > 0).length;

  const renderConvoList = (state: ConvoTabState, onPageChange: (p: number) => void) => {
    if (state.loading) {
      return (
        <div className="communications-page__empty communications-page__empty--center">
          Loading…
        </div>
      );
    }
    if (state.error) {
      return <div className="communications-page__error">{state.error}</div>;
    }
    if (state.items.length === 0) {
      return (
        <div className="communications-page__empty communications-page__empty--center">
          <span className="communications-page__empty-icon">✉</span>
          No conversations yet.
        </div>
      );
    }
    return (
      <>
        <div className="communications-page__list">
          {state.items.map((conv, i) => (
            <button
              key={`conv-${conv.contactId}-${conv.channel}-${i}`}
              className={`communications-page__convo-row${
                conv.unreadCount > 0 ? " communications-page__convo-row--unread" : ""
              }`}
              onClick={() => handleConvoClick(conv)}
            >
              <span className="communications-page__convo-icon">
                <ChannelIcon channel={conv.channel} />
              </span>
              <div className="communications-page__convo-body">
                <div className="communications-page__convo-top">
                  <span className="communications-page__convo-name">
                    {contactLabel(conv.contactType)} · {conv.contactName}
                  </span>
                  <span className="communications-page__convo-time">
                    {formatRelativeTime(conv.lastMessageAt)}
                  </span>
                </div>
                {conv.lastMessageSnippet && (
                  <div className="communications-page__convo-snippet">
                    {conv.lastMessageSnippet.length > 80
                      ? conv.lastMessageSnippet.slice(0, 80) + "…"
                      : conv.lastMessageSnippet}
                  </div>
                )}
              </div>
              {conv.unreadCount > 0 && (
                <span className="communications-page__badge">
                  {conv.unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>
        <TablePagination
          currentPage={state.currentPage}
          pageSize={PAGE_SIZE}
          totalCount={state.totalCount}
          onPageChange={onPageChange}
        />
      </>
    );
  };

  const renderUnmatchedTab = () => {
    if (unmatched.loading) {
      return (
        <div className="communications-page__empty communications-page__empty--center">
          Loading…
        </div>
      );
    }
    if (unmatched.error) {
      return <div className="communications-page__error">{unmatched.error}</div>;
    }
    if (unmatched.items.length === 0) {
      return (
        <div className="communications-page__empty communications-page__empty--center">
          No unmatched messages.
        </div>
      );
    }
    return (
      <>
        <div className="communications-page__list">
          {unmatched.items.map((msg) => {
            const isExpanded = expandedIds.has(msg.id);
            const preview = msg.body
              ? msg.body.length > 60
                ? msg.body.slice(0, 60) + "…"
                : msg.body
              : null;
            return (
              <div
                key={msg.id}
                className={`communications-page__unmatched-item${
                  isExpanded ? " communications-page__unmatched-item--expanded" : ""
                }`}
              >
                <div
                  className="communications-page__unmatched-summary"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={() => toggleExpanded(msg.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") toggleExpanded(msg.id);
                  }}
                >
                  <span className="communications-page__convo-icon">
                    <ChannelIcon channel={msg.channel} />
                  </span>
                  <div className="communications-page__unmatched-summary-body">
                    {msg.senderIdentifier && (
                      <span className="communications-page__unmatched-sender">
                        {msg.senderIdentifier}
                      </span>
                    )}
                    {msg.subject && (
                      <span className="communications-page__unmatched-subject">
                        {msg.subject}
                      </span>
                    )}
                    {!isExpanded && preview && (
                      <span className="communications-page__convo-snippet">
                        {preview}
                      </span>
                    )}
                  </div>
                  <button
                    className="communications-page__btn communications-page__btn--assign communications-page__btn--sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAssigningMessage(msg);
                    }}
                  >
                    Assign
                  </button>
                  <button
                    className="communications-page__btn communications-page__btn--dismiss communications-page__btn--sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDismiss(msg.id);
                    }}
                    disabled={dismissing === msg.id}
                  >
                    {dismissing === msg.id ? "…" : "Dismiss"}
                  </button>
                  <span className="communications-page__convo-time">
                    {formatRelativeTime(msg.occurredAt)}
                  </span>
                </div>
                {isExpanded && msg.body && (
                  <div className="communications-page__unmatched-expanded">
                    <div className="communications-page__unmatched-body">
                      {msg.body}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <TablePagination
          currentPage={unmatched.currentPage}
          pageSize={PAGE_SIZE}
          totalCount={unmatched.totalCount}
          onPageChange={loadUnmatchedPage}
        />
      </>
    );
  };

  const renderOutreachTab = () => {
    if (outreachLoading && !outreachLoaded) {
      return (
        <div className="communications-page__empty communications-page__empty--center">
          Loading…
        </div>
      );
    }
    if (outreachError) {
      return <div className="communications-page__error">{outreachError}</div>;
    }
    if (outreachRecords.length === 0 && outreachLoaded) {
      return (
        <div className="communications-page__empty communications-page__empty--center">
          No bulk outreach records yet.
        </div>
      );
    }
    return (
      <>
        <div className="communications-page__list">
          {outreachRecords.map((record) => {
            const isExpanded = expandedOutreach === record.id;
            return (
              <div key={record.id} className="communications-page__outreach-item">
                <div
                  className="communications-page__outreach-summary"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleExpandOutreach(record.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleExpandOutreach(record.id);
                  }}
                >
                  <span className="communications-page__convo-icon">
                    <ChannelIcon channel={record.channel} />
                  </span>
                  <div className="communications-page__outreach-body">
                    <div className="communications-page__outreach-top">
                      <span className="communications-page__outreach-count">
                        Sent to {record.totalRecipients} coach{record.totalRecipients !== 1 ? "es" : ""}
                      </span>
                      {record.players && record.players.length > 0 && (
                        <span className="communications-page__outreach-player">
                          on behalf of{" "}
                          {record.players.map((p, i) => (
                            <span key={p.id}>
                              <button
                                type="button"
                                className="communications-page__outreach-player-link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/crm/contacts/${p.id}`, { state: backToMessages });
                                }}
                              >
                                {p.name}
                              </button>
                              {i < record.players.length - 1 ? ", " : ""}
                            </span>
                          ))}
                        </span>
                      )}
                    </div>
                    {record.subject && (
                      <div className="communications-page__convo-snippet">{record.subject}</div>
                    )}
                    {!record.subject && record.messagePreview && (
                      <div className="communications-page__convo-snippet">{record.messagePreview}</div>
                    )}
                  </div>
                  <div className="communications-page__outreach-stats">
                    {record.successCount > 0 && (
                      <span className="communications-page__outreach-stat communications-page__outreach-stat--success">
                        {record.successCount} sent
                      </span>
                    )}
                    {record.responseCount > 0 && (
                      <span className="communications-page__outreach-stat communications-page__outreach-stat--responded">
                        {record.responseCount} responded
                      </span>
                    )}
                    {record.failureCount > 0 && (
                      <span className="communications-page__outreach-stat communications-page__outreach-stat--failure">
                        {record.failureCount} failed
                      </span>
                    )}
                  </div>
                  <span className="communications-page__convo-time">
                    {formatRelativeTime(record.sentAt)}
                  </span>
                </div>

                {isExpanded && (
                  <div className="communications-page__outreach-detail">
                    {detailLoading ? (
                      <div className="communications-page__outreach-detail-loading">Loading details...</div>
                    ) : outreachDetail && outreachDetail.id === record.id ? (
                      <>
                        {outreachDetail.messageBody && (
                          <div className="communications-page__outreach-message">
                            {outreachDetail.subject && (
                              <div className="communications-page__outreach-subject">{outreachDetail.subject}</div>
                            )}
                            <div className="communications-page__outreach-body-text">{outreachDetail.messageBody}</div>
                          </div>
                        )}
                        {outreachDetail.sentByName && (
                          <div className="communications-page__outreach-sent-by">Sent by {outreachDetail.sentByName}</div>
                        )}
                        <div className="communications-page__outreach-recipients">
                          <div className="communications-page__outreach-recipients-header">
                            Recipients ({outreachDetail.recipients.length})
                          </div>
                          {outreachDetail.recipients.map((r) => (
                            <div key={r.id} className="communications-page__outreach-recipient">
                              {r.coachId ? (
                                <button
                                  type="button"
                                  className="communications-page__outreach-recipient-name communications-page__outreach-recipient-name--link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/crm/contacts/${r.coachId}`, { state: backToMessages });
                                  }}
                                >
                                  {r.coachName}
                                </button>
                              ) : (
                                <span className="communications-page__outreach-recipient-name">{r.coachName}</span>
                              )}
                              {r.coachSchool && (
                                <span className="communications-page__outreach-recipient-school">{r.coachSchool}</span>
                              )}
                              {r.coachEmail && (
                                <span className="communications-page__outreach-recipient-email">{r.coachEmail}</span>
                              )}
                              <span className={`communications-page__outreach-recipient-status communications-page__outreach-recipient-status--${r.status.toLowerCase()}`}>
                                {r.status}
                              </span>
                              {r.firstResponseAt && (
                                <span className="communications-page__outreach-recipient-status communications-page__outreach-recipient-status--responded">
                                  Coach Responded
                                </span>
                              )}
                              {r.failureReason && (
                                <span className="communications-page__outreach-recipient-error">{r.failureReason}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <TablePagination
          currentPage={outreachPage}
          pageSize={PAGE_SIZE}
          totalCount={outreachTotalCount}
          onPageChange={loadOutreachPage}
        />
      </>
    );
  };

  return (
    <div className="communications-page">
      <div className="communications-page__header">
        <h2 className="communications-page__title">Messages</h2>
      </div>

      <div className="communications-page__card">
        <div className="communications-page__tab-bar">
          <button
            className={`communications-page__tab${activeTab === "recent" ? " communications-page__tab--active" : ""}`}
            onClick={() => handleTabChange("recent")}
          >
            Recent
            {recent.totalCount > 0 && (
              <span className="communications-page__tab-badge">{recent.totalCount}</span>
            )}
          </button>
          <button
            className={`communications-page__tab${activeTab === "unread" ? " communications-page__tab--active" : ""}`}
            onClick={() => handleTabChange("unread")}
          >
            Unread
            {unreadBadgeCount > 0 && (
              <span className="communications-page__tab-badge communications-page__tab-badge--unread">
                {unreadBadgeCount}
              </span>
            )}
          </button>
          <button
            className={`communications-page__tab${activeTab === "unmatched" ? " communications-page__tab--active" : ""}`}
            onClick={() => handleTabChange("unmatched")}
          >
            Unmatched
            {unmatched.totalCount > 0 && (
              <span className="communications-page__tab-badge communications-page__tab-badge--unmatched">
                {unmatched.totalCount}
              </span>
            )}
          </button>
          <button
            className={`communications-page__tab${activeTab === "outreach" ? " communications-page__tab--active" : ""}`}
            onClick={() => handleTabChange("outreach")}
          >
            Outreach
          </button>
        </div>

        <div className="communications-page__tab-content">
          {activeTab === "recent" &&
            renderConvoList(recent, (p) => loadConvoPage("recent", p))}
          {activeTab === "unread" && (
            unread.items.length === 0 && !unread.loading && unread.isLoaded ? (
              <div className="communications-page__empty communications-page__empty--center">
                <span className="communications-page__empty-icon">✓</span>
                All caught up.
              </div>
            ) : (
              renderConvoList(unread, (p) => loadConvoPage("unread", p))
            )
          )}
          {activeTab === "unmatched" && renderUnmatchedTab()}
          {activeTab === "outreach" && renderOutreachTab()}
        </div>
      </div>

      {assigningMessage && (
        <AssignMessageModal
          message={assigningMessage}
          onClose={() => setAssigningMessage(null)}
          onAssigned={handleAssigned}
        />
      )}
    </div>
  );
};
